import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Star, Send, Trash2, Mail, Clock, CheckCircle, XCircle,
  AlertCircle, Loader, MessageSquare, ChevronDown, ChevronUp
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReviewInvitations = () => {
  const [reviews, setReviews] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [email, setEmail] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [expandedReview, setExpandedReview] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [respondingTo, setRespondingTo] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Load reviews and invitations in parallel
      const [reviewsRes, invitationsRes] = await Promise.all([
        axios.get(`${API}/provider/my-reviews`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/provider/review-invitations`, { headers })
      ]);

      setReviews(reviewsRes.data || []);
      setInvitations(invitationsRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const sendInvitation = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Bitte geben Sie eine E-Mail-Adresse ein');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/provider/review-invitations/send`,
        { customer_email: email, customer_name: customerName || null, frontend_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Einladung erfolgreich gesendet');
      setEmail('');
      setCustomerName('');
      loadData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler beim Senden der Einladung';
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  const deleteInvitation = async (invitationId) => {
    if (!window.confirm('Möchten Sie diese Einladung wirklich löschen?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/provider/review-invitations/${invitationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Einladung gelöscht');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Löschen der Einladung');
    }
  };

  const respondToReview = async (reviewId) => {
    if (!responseText.trim()) {
      toast.error('Bitte geben Sie eine Antwort ein');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/reviews/${reviewId}/response`,
        { response: responseText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Antwort gespeichert');
      setRespondingTo(null);
      setResponseText('');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Speichern der Antwort');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
            <Clock className="w-3 h-3" />
            Ausstehend
          </span>
        );
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
            <CheckCircle className="w-3 h-3" />
            Abgeschlossen
          </span>
        );
      case 'expired':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
            <XCircle className="w-3 h-3" />
            Abgelaufen
          </span>
        );
      default:
        return null;
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Statistics
  const stats = {
    totalReviews: reviews.length,
    averageRating: reviews.length > 0
      ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
      : 0,
    pendingInvitations: invitations.filter(i => i.status === 'pending').length,
    completedInvitations: invitations.filter(i => i.status === 'completed').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-[#2B2142]" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-1">
            <Star className="w-5 h-5 text-yellow-500" />
            <span className="text-sm text-gray-600">Bewertungen</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.totalReviews}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-1">
            <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
            <span className="text-sm text-gray-600">Durchschnitt</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.averageRating}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-5 h-5 text-yellow-600" />
            <span className="text-sm text-gray-600">Ausstehend</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.pendingInvitations}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-sm text-gray-600">Beantwortet</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.completedInvitations}</p>
        </div>
      </div>

      {/* Received Reviews Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 border-b border-yellow-100">
          <h2 className="text-xl font-bold text-gray-900">Erhaltene Bewertungen</h2>
          <p className="text-gray-600 text-sm mt-1">
            Alle Bewertungen, die Ihre Kunden abgegeben haben
          </p>
        </div>

        <div className="p-6">
          {reviews.length === 0 ? (
            <div className="text-center py-8">
              <Star className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Noch keine Bewertungen erhalten</p>
              <p className="text-gray-400 text-sm mt-1">
                Laden Sie Ihre Kunden ein, eine Bewertung abzugeben
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {reviews.map((review) => (
                <div
                  key={review.id}
                  className="border border-gray-200 rounded-xl p-4 hover:border-yellow-300 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        {renderStars(review.rating)}
                        <span className="text-sm text-gray-500">{formatDate(review.created_at)}</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 break-words">{review.title}</h4>
                      <p className="text-sm text-gray-600 mt-1 break-words">{review.comment}</p>
                      <p className="text-xs text-gray-400 mt-2">— {review.customer_name}</p>

                      {review.provider_response && (
                        <div className="mt-3 p-3 bg-[#FFF0D1]/30 rounded-lg">
                          <p className="text-xs text-[#2B2142] font-medium mb-1">Ihre Antwort:</p>
                          <p className="text-sm text-[#2B2142] break-words">{review.provider_response}</p>
                        </div>
                      )}
                    </div>

                    <button
                      onClick={() => {
                        setExpandedReview(expandedReview === review.id ? null : review.id);
                        setRespondingTo(review.id);
                        setResponseText(review.provider_response || '');
                      }}
                      className="text-gray-400 hover:text-gray-600 p-2"
                    >
                      {expandedReview === review.id ? (
                        <ChevronUp className="w-5 h-5" />
                      ) : (
                        <MessageSquare className="w-5 h-5" />
                      )}
                    </button>
                  </div>

                  {expandedReview === review.id && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <textarea
                        value={responseText}
                        onChange={(e) => setResponseText(e.target.value)}
                        placeholder="Ihre Antwort auf diese Bewertung..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-yellow-500 focus:outline-none text-sm"
                        rows={3}
                      />
                      <div className="flex justify-end gap-2 mt-2">
                        <button
                          onClick={() => {
                            setExpandedReview(null);
                            setRespondingTo(null);
                            setResponseText('');
                          }}
                          className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
                        >
                          Abbrechen
                        </button>
                        <button
                          onClick={() => respondToReview(review.id)}
                          className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg text-sm font-medium"
                        >
                          Antwort speichern
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Send Invitation Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 border-b border-yellow-100">
          <h2 className="text-xl font-bold text-gray-900">Kunden zur Bewertung einladen</h2>
          <p className="text-gray-600 text-sm mt-1">
            Senden Sie Ihren Kunden eine E-Mail mit einem einmaligen Bewertungslink
          </p>
        </div>

        <div className="p-6">
          <form onSubmit={sendInvitation} className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="E-Mail-Adresse des Kunden"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-yellow-500 focus:outline-none"
                required
              />
            </div>
            <div className="flex-1 sm:max-w-[200px]">
              <input
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="Name (optional)"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-yellow-500 focus:outline-none"
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              className="px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {sending ? (
                <Loader className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Einladen
                </>
              )}
            </button>
          </form>

          <p className="text-xs text-gray-500 mt-3">
            <AlertCircle className="w-3 h-3 inline mr-1" />
            Der Einladungslink ist 14 Tage gültig und kann nur einmal verwendet werden.
          </p>
        </div>
      </div>

      {/* Invitations List */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <h3 className="font-bold text-gray-900">Gesendete Einladungen</h3>
        </div>

        <div className="p-6">
          {invitations.length === 0 ? (
            <div className="text-center py-8">
              <Mail className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Noch keine Einladungen gesendet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {invitations.map((invitation) => (
                <div
                  key={invitation.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-xl"
                >
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="font-medium text-gray-900">
                        {invitation.customer_name || invitation.customer_email}
                      </p>
                      {invitation.customer_name && (
                        <p className="text-sm text-gray-500">{invitation.customer_email}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        Gesendet am {formatDate(invitation.created_at)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {getStatusBadge(invitation.status)}
                    {invitation.status === 'pending' && (
                      <button
                        onClick={() => deleteInvitation(invitation.id)}
                        className="text-red-500 hover:text-red-700 p-2"
                        title="Einladung löschen"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewInvitations;
